<script setup>
import {computed, ref} from 'vue'
import UserAvatar from '@/common/UserAvatar'
import vUserAccess from '@/vue/directives/userAccess'
import Button from '@/common/Button.vue'
import UserProfileDropdownItem from "@/common/user-profile/UserProfileDropdownItem.vue"
import {useToastsStore} from '@/stores/toasts'
import {useUserStore} from '@/stores/user'
import {useModalsStore} from '@/stores/modals'
import {useAppStore} from '@/stores/appStore'
import {useRoute, useRouter} from "vue-router"

const toastsStore = useToastsStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()
const appStore = useAppStore()

const router = useRouter()
const route = useRoute()

const dropdown = ref(null)

const isActive = computed(() => ['/settings', '/subscription-plan', '/users', '/users_groups'].indexOf(route.path) > -1)
const user = computed(() => userStore.user)
const reportsLink = computed(() => ['bigbrandbeds', 'outdoorsproject', 'printgifts'].findIndex(str => (appStore.sitename || '').startsWith(str)) > -1)
const darkMode = computed(() => !!userStore.pref('darkMode'))
const showClearTheCache = computed(() => user.value?.administrator || user.value?.allowCacheClearance)
const betaMode = computed(() => appStore.betaMode)

const subscriptionNavItemEnabled = () => {
    return (
        appStore.settings?.subscriptionPriceId
        && (user.value?.administrator || user.value?.access?.__subscriptionPlanAccess)
    )
}

const goTo = (tableName) => {
    router.push({path: '/' + tableName})

    // Simulate clicking the trigger button to dismiss the dropdown natively
    dropdown.value?.childNodes[0]?.click()
}

const clearTheCache = async () => {
    const {notificationId} = await toastsStore.notification({
        title: 'Clear the cache',
        message: 'Starting&hellip;'
    })

    await appStore.clearCache({notificationId})
}

const logout = () => {
    modalsStore.hideAll()
    localStorage.removeItem('token')
    userStore.reset()
    window.location.reload()
}

const toggleDarkMode = () => {
    userStore.toggleDarkMode()
}
</script>

<template>
    <div ref="dropdown" class="dropdown">
        <Button :class="{ active: isActive }" class="h-100 rounded-0" data-bs-toggle="dropdown"
                title="Settings">
            <UserAvatar v-if="user" :user-id="user.id" class="navigation-user-avatar"/>
        </Button>

        <div class="dropdown-menu dropdown-menu-end">
            <button v-user-access="'account_settings'" :class="{ active: route.path === '/settings' }"
                    class="dropdown-item" type="button" @click="goTo('settings')">
                <span class="me-2 bi bi-gear"/>
                Settings
            </button>

            <button v-if="subscriptionNavItemEnabled()" :class="{ active: route.path === '/subscription-plan' }"
                    class="dropdown-item" type="button" @click="goTo('subscription-plan')">
                Subscription plan
            </button>

            <UserProfileDropdownItem/>

            <button v-user-access="'users'" :class="{ active: route.path === '/users' }" class="dropdown-item"
                    type="button" @click="goTo('users')">
                <span class="me-2 bi bi-people"/>
                Manage users
            </button>

            <button v-user-access="'users_groups'" :class="{ active: route.path === '/users_groups' }"
                    class="dropdown-item" type="button" @click="goTo('users_groups')">
                <span class="me-2 bi bi-people"/>
                Manage roles
            </button>

            <router-link v-if="reportsLink" :to="{ name: 'reports' }" class="dropdown-item">
                <span class="me-2 bi bi-bar-chart-line"/>
                Reports
            </router-link>

            <button v-if="showClearTheCache"
                    :class="{ 'clear-the-cache-item-active': appStore.cacheNeedsClearing }"
                    class="dropdown-item clear-the-cache-item d-flex align-items-center" type="button"
                    @click="clearTheCache">
                <span class="me-2 bi bi-trash"/>
                Clear the cache
            </button>

            <button class="dropdown-item d-flex align-items-center" type="button" @click="toggleDarkMode">
                <i class="me-2 bi" :class="{'bi-sun': darkMode, 'bi-moon': !darkMode}"/>
                {{ darkMode ? 'Light mode' : 'Dark mode' }}
            </button>

            <button class="dropdown-item d-flex align-items-center" type="button" @click="logout">
                <span class="me-2 bi bi-box-arrow-right"/>
                Log out
            </button>
        </div>
    </div>
</template>

<style scoped>
.clear-the-cache-item,
.clear-the-cache-item:hover {
    color: #999;
}

.clear-the-cache-item-active,
.clear-the-cache-item-active:hover {
    color: salmon;
}
</style>